import { Typography } from "@povio/ui";
import { cx } from "class-variance-authority";
import { useFlags } from "launchdarkly-react-client-sdk";
import Link from "next/link";
import { useRouter } from "next/router";

import { AddUserIcon } from "@/assets/icons/general/AddUser";
import {
  BriefcaseIcon,
  ChartBarIcon,
  CompassIcon,
  FolderIcon,
  HexagonIcon,
  ShoppingBagIcon,
  TVIcon,
  TargetIcon,
  UserIcon,
} from "@/assets/icons/navbar";
import { WalletIcon } from "@/assets/icons/navbar/WalletIcon";
import { PaperIcon } from "@/assets/icons/ui/PaperIcon";
import { isPathActive } from "@/components/shared/layout/sidebar/Sidebar";
import { RouteConfig } from "@/config/route.config";
import { useGeoLocation } from "@/hooks/useGeoLocation";

interface NavigationItemProps {
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  text: string;
  href: string;
  isActive: boolean;
  isExternal?: boolean;
}

const navigationItems: Omit<NavigationItemProps, "isActive">[] = [
  {
    icon: UserIcon,
    text: "Career Profile",
    href: RouteConfig.Home,
  },
  {
    icon: FolderIcon,
    text: "Investment Portfolio",
    href: RouteConfig.InvestmentPortfolio,
  },
  {
    icon: ChartBarIcon,
    text: "Analytics",
    href: RouteConfig.Analytics,
  },
  {
    icon: HexagonIcon,
    text: "Rewards",
    href: RouteConfig.MyRewards,
  },
  {
    icon: BriefcaseIcon,
    text: "Tom & Scott's Portfolio",
    href: RouteConfig.VipPortfolios,
  },
  {
    icon: CompassIcon,
    text: "Comp Ratio",
    href: RouteConfig.CeoVsEmployee,
  },
  {
    icon: TVIcon,
    text: "One Lucky Dog",
    href: RouteConfig.Liveshow,
  },
  {
    icon: ShoppingBagIcon,
    text: "Swag",
    href: RouteConfig.External.Swag,
    isExternal: true,
  },
  {
    icon: WalletIcon,
    text: "My Wallet",
    href: RouteConfig.MyWallet,
  },
  {
    icon: TargetIcon,
    text: "Prediction",
    href: RouteConfig.PredictiveSalary,
  },
  {
    icon: AddUserIcon,
    text: "Guest Calculation",
    href: RouteConfig.Guest.Index,
  },
  {
    icon: PaperIcon,
    text: "News & Insights",
    href: RouteConfig.News,
  },
];

export const DesktopNavbar = () => {
  const router = useRouter();
  const { cryptoWallet } = useFlags();
  const { data: geoLocation } = useGeoLocation();

  // NY compliance check: IP-based detection via CloudFront headers
  const isNYUser = geoLocation?.isNYUser ?? false;

  // Filter out My Wallet if conditions aren't met
  const shouldShowWallet = cryptoWallet && !isNYUser;

  const getIsNavItemActive = (href: string) => {
    if (href === RouteConfig.Home && (router.asPath === "/" || router.asPath === "/profile/")) {
      return true;
    }
    return isPathActive(href, router.asPath);
  };

  const filteredNavigationItems = navigationItems.filter((item) => {
    // Hide My Wallet if conditions aren't met
    if (item.href === RouteConfig.MyWallet && !shouldShowWallet) {
      return false;
    }
    return true;
  });

  return (
    <div className="no-scrollbar z-10 flex h-[calc(100dvh-70px)] min-w-26 flex-col overflow-y-auto border-elevation-outline-default-1 border-r bg-elevation-fill-default-0">
      {filteredNavigationItems.map((item) => (
        <NavigationItem
          isActive={getIsNavItemActive(item.href)}
          key={item.href}
          {...item}
        />
      ))}
    </div>
  );
};

const NavigationItem = ({ icon, text, href, isActive, isExternal }: NavigationItemProps) => {
  const Icon = icon;
  return (
    <Link
      href={href}
      target={isExternal ? "_blank" : undefined}
    >
      <div
        className={cx(
          "relative flex w-full flex-col items-center gap-1 px-3 py-4",
          isActive ? "desktop-navbar-active-indicator text-interactive-text-primary-idle" : "text-text-default-3",
        )}
      >
        <Icon className="h-6 w-6" />
        <Typography
          size="label-4"
          variant="prominent-1"
          className="text-center text-[10px]! leading-4!"
        >
          {text}
        </Typography>
        {isActive && (
          <div className="absolute top-0 right-0 bottom-0 h-full w-[3px] bg-interactive-text-primary-idle" />
        )}
      </div>
    </Link>
  );
};
